import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Button, TextField, Stack,
  IconButton, Tab, Tabs,
  Tooltip, Alert, CircularProgress, LinearProgress, Divider,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  ExpandMore, CheckCircle, Cancel, PlayArrow,
  Refresh, FileDownload, Search, Science, BugReport,
  RadioButtonUnchecked, RemoveCircleOutline,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import ApiHealthTab from '../components/ApiHealthTab';

// ─── Types ────────────────────────────────────────────────────────────────────

interface QATestCase {
  id: string;
  category: string;
  severity: 'P0' | 'P1' | 'P2' | 'P3';
  title: string;
  expectedBehavior: string;
  knownStatus: 'PASS' | 'FAIL' | 'UNTESTED' | 'PENDING_IMPL';
  automatable: boolean;
  runId?: string;
  bugRef?: string;
  fixedInVersion?: string;
  source?: string;
}

interface SessionResult {
  status: 'PASS' | 'FAIL' | 'SKIP' | null;
  notes: string;
  runAt?: string;
  autoResult?: { status: string; detail: string; durationMs: number };
}

type FilterType = 'all' | 'untested' | 'pass' | 'fail' | 'automated';

// ─── Flow Structure (all 288 tests organized into QA journeys) ────────────────

interface SubFlow { name: string; tests: string[] }
interface Flow { name: string; icon: string; description: string; subFlows: SubFlow[] }

const FLOW_STRUCTURE: Flow[] = [
  {
    name: 'SS User Journey',
    icon: '📱',
    description: 'Complete SweatSocial app experience — registration to booking',
    subFlows: [
      { name: 'Registration & Auth', tests: ['AUTH-01', 'AUTH-05', 'AUTH-06', 'AUTH-07', 'AUTH-08', 'USR-05', 'USR-07', 'USR-21', 'USR-22'] },
      { name: 'Studio & Session Discovery', tests: ['STU-11', 'STU-17', 'STU-19', 'SES-01', 'SES-19', 'SES-20', 'SES-21', 'SES-22', 'SES-24', 'SES-25', 'SES-26'] },
      { name: 'Trial Class Eligibility', tests: ['USR-16', 'USR-17', 'SES-24'] },
      { name: 'Pack Purchase', tests: ['PCK-01', 'PCK-03', 'PCK-06', 'PCK-07', 'PCK-08', 'PCK-09', 'PCK-10', 'PCK-16', 'PCK-24', 'PCK-27', 'WBH-13', 'WBH-14'] },
      { name: 'Booking', tests: ['BKG-01', 'BKG-04', 'BKG-05', 'BKG-06', 'BKG-07', 'BKG-13', 'BKG-14', 'BKG-26', 'BKG-27', 'BKG-28', 'BKG-30', 'BKG-32', 'BKG-34', 'BKG-36'] },
      { name: 'QR Check-In', tests: ['QR-01', 'QR-02', 'QR-03', 'QR-04', 'QR-05', 'QR-06', 'QR-07', 'QR-12', 'QR-16', 'QR-20', 'SES-27'] },
      { name: 'Cancellations', tests: ['BKG-10', 'BKG-11', 'BKG-35', 'SES-23', 'SES-28', 'BKG-31'] },
      { name: 'Notifications', tests: ['BKG-08', 'BKG-37', 'BKG-38', 'NTF-01', 'NTF-04', 'NTF-05', 'NTF-09', 'NTF-20'] },
    ],
  },
  {
    name: 'PP (Purple Pilates) Journey',
    icon: '💜',
    description: 'Purple Pilates whitelabel app — isolation + PP-specific flows',
    subFlows: [
      { name: 'Registration & Auth', tests: ['AUTH-02', 'AUTH-04', 'USR-02', 'USR-06', 'USR-07'] },
      { name: 'Isolation: Studio & Session', tests: ['STU-12', 'SES-05', 'SES-06', 'SES-07', 'SES-08', 'EVT-10', 'USR-14', 'USR-15'] },
      { name: 'Pack Purchase (PP)', tests: ['PCK-02', 'PCK-29', 'PCK-30'] },
      { name: 'Booking (PP)', tests: ['BKG-02', 'BKG-18', 'BKG-19', 'BKG-25'] },
      { name: 'Notifications (PP device)', tests: ['BKG-09', 'BKG-12', 'NTF-02', 'NTF-03', 'NTF-06', 'NTF-07', 'NTF-10', 'NTF-11', 'NTF-15'] },
    ],
  },
  {
    name: 'Partner API Journey',
    icon: '🤝',
    description: 'External partner integration via API key — booking, FCM, logs',
    subFlows: [
      { name: 'Auth & Rate Limiting', tests: ['AUTH-03', 'AUTH-14', 'AUTH-15', 'AUTH-16', 'PAR-01', 'PAR-02', 'PAR-03', 'PAR-04', 'PAR-18'] },
      { name: 'Studio Scoping', tests: ['PAR-05', 'PAR-14', 'PAR-21'] },
      { name: 'Booking & Pack Operations', tests: ['PAR-06', 'PAR-07', 'PAR-08', 'PAR-10', 'PAR-11', 'PAR-15', 'PAR-16', 'PAR-19', 'PAR-20', 'PAR-22'] },
      { name: 'FCM Token Storage', tests: ['PAR-09', 'NTF-11'] },
      { name: 'Logging & TTL', tests: ['PAR-12', 'PAR-13'] },
    ],
  },
  {
    name: 'Pack Lifecycle',
    icon: '📦',
    description: 'Pack purchase → active → freeze → expire → notifications',
    subFlows: [
      { name: 'Purchase & Activation', tests: ['PCK-01', 'PCK-02', 'PCK-03', 'PCK-13', 'PCK-16', 'PCK-17', 'PCK-18', 'PCK-19', 'PCK-20'] },
      { name: 'Pack Sharing', tests: ['USR-09', 'USR-10', 'PCK-14', 'PCK-15'] },
      { name: 'Freeze & Unfreeze', tests: ['FRZ-01', 'FRZ-02', 'FRZ-03', 'FRZ-04', 'FRZ-05', 'FRZ-06', 'FRZ-07', 'FRZ-08', 'FRZ-10', 'FRZ-11', 'FRZ-13', 'FRZ-15', 'FRZ-16'] },
      { name: 'Freeze: Duration Mode', tests: ['FRZ-03', 'FRZ-04'] },
      { name: 'Expiry & Notifications', tests: ['PCK-04', 'PCK-05', 'PCK-24', 'PCK-27', 'CMP-03'] },
    ],
  },
  {
    name: 'Manual Walk-In & QR',
    icon: '📍',
    description: 'Admin manual check-in flows + QR code validation edge cases',
    subFlows: [
      { name: 'Manual Walk-In', tests: ['BKG-03', 'BKG-23', 'BKG-24', 'QR-08', 'QR-09', 'QR-10', 'QR-11', 'QR-14'] },
      { name: 'Admin Panel Walk-In Dialog', tests: ['QR-17', 'ADM-09', 'ADM-10'] },
      { name: 'QR Edge Cases', tests: ['QR-05', 'QR-06', 'QR-07', 'QR-16', 'QR-19', 'BKG-21', 'BKG-22'] },
    ],
  },
  {
    name: 'Session Types & Waitlist',
    icon: '🎯',
    description: 'Group / Private / Duet sessions, waitlist auto-promotion',
    subFlows: [
      { name: 'Session Types', tests: ['SES-02', 'SES-03', 'SES-04', 'SES-09', 'SES-19'] },
      { name: 'Waitlist', tests: ['SES-15', 'SES-16', 'SES-17', 'SES-18', 'BKG-25'] },
      { name: 'No-Show & Late Cancel', tests: ['SES-14', 'SES-28', 'CMP-01', 'CMP-02'] },
    ],
  },
  {
    name: 'FCM Routing Audit',
    icon: '🔔',
    description: 'Critical — verify push notifications reach the correct app/device',
    subFlows: [
      { name: 'resolveUserToken Logic (automated)', tests: ['NTF-01', 'NTF-02', 'NTF-03', 'NTF-04', 'NTF-05', 'USR-03', 'USR-04'] },
      { name: 'Booking Notifications', tests: ['BKG-08', 'BKG-09', 'BKG-12', 'NTF-06', 'NTF-07'] },
      { name: 'Cron Notifications', tests: ['SES-11', 'SES-12', 'SES-13', 'EVT-07', 'PCK-04', 'PCK-05', 'CMP-05', 'CMP-06'] },
      { name: 'Studio Owner Notifications', tests: ['NTF-17', 'NTF-18', 'STU-13', 'STU-14', 'BKG-37'] },
      { name: 'Admin Campaigns', tests: ['NTF-13', 'NTF-22', 'ADM-03', 'ADM-04'] },
    ],
  },
  {
    name: 'Studio Configuration',
    icon: '🏋️',
    description: 'Studio setup, branches, GST, Razorpay, whitelabel config',
    subFlows: [
      { name: 'Studio Creation & Razorpay', tests: ['STU-01', 'STU-02', 'STU-03', 'STU-04', 'STU-05'] },
      { name: 'Branches', tests: ['STU-06', 'STU-07', 'USR-19'] },
      { name: 'Capacity & Session Types', tests: ['STU-09', 'SES-09', 'STU-08'] },
      { name: 'GST Setup', tests: ['STU-15', 'STU-16', 'INV-02', 'INV-03'] },
      { name: 'Notification Preferences', tests: ['STU-13', 'STU-14', 'NTF-17', 'NTF-18'] },
      { name: 'Deactivation', tests: ['STU-18'] },
    ],
  },
  {
    name: 'Revenue & Payments',
    icon: '💰',
    description: 'Razorpay webhooks, invoices, revenue reporting, GST',
    subFlows: [
      { name: 'Razorpay Webhooks', tests: ['WBH-01', 'WBH-02', 'WBH-03', 'WBH-04', 'WBH-05', 'WBH-06', 'WBH-07', 'WBH-08', 'WBH-09', 'WBH-10', 'WBH-11', 'WBH-12'] },
      { name: 'Invoice Generation', tests: ['INV-01', 'INV-02', 'INV-03', 'INV-05', 'INV-07', 'INV-10', 'BKG-32'] },
      { name: 'Revenue Reporting', tests: ['REV-01', 'REV-02', 'REV-03', 'REV-04', 'REV-05', 'REV-06', 'REV-09', 'STU-05', 'STU-20'] },
      { name: 'Email Receipts', tests: ['EML-01', 'EML-02', 'EML-03', 'EML-04', 'EML-05', 'EML-07', 'EML-08', 'BKG-38'] },
    ],
  },
  {
    name: 'Admin Panel',
    icon: '⚙️',
    description: 'Admin panel features — access control, user/studio management, reporting',
    subFlows: [
      { name: 'Access Control', tests: ['ADM-01', 'ADM-02', 'AUTH-09', 'AUTH-10', 'AUTH-17', 'AX-06', 'ADM-11'] },
      { name: 'Studio Management', tests: ['ADM-05', 'ADM-06', 'ADM-07', 'ADM-08', 'STU-08'] },
      { name: 'Manual Booking Dialog', tests: ['ADM-09', 'ADM-10', 'QR-17'] },
      { name: 'User Management', tests: ['USR-11', 'USR-12', 'USR-13', 'USR-18', 'ADM-14'] },
      { name: 'Notifications Panel', tests: ['ADM-03', 'ADM-04', 'NTF-22', 'NTF-24'] },
      { name: 'QA & System Tools', tests: ['ADM-15', 'ADM-17', 'ADM-18', 'CMP-10'] },
    ],
  },
  {
    name: 'Whitelabel Isolation Matrix',
    icon: '🔒',
    description: 'Critical — verify no data/notification leaks between apps',
    subFlows: [
      { name: 'Booking Isolation', tests: ['ISO-01', 'ISO-02', 'ISO-11', 'ISO-12', 'ISO-13'] },
      { name: 'FCM / Notification Isolation', tests: ['ISO-03', 'ISO-04', 'ISO-10', 'ISO-14', 'ISO-15'] },
      { name: 'Pack & Studio Isolation', tests: ['ISO-05', 'ISO-06', 'ISO-07', 'ISO-08'] },
      { name: 'Revenue Isolation', tests: ['ISO-09', 'ISO-16', 'ISO-17', 'ISO-18', 'ISO-19', 'ISO-20'] },
    ],
  },
  {
    name: 'Cron Jobs',
    icon: '⏰',
    description: 'All 10 background cron jobs — scheduling, correctness, idempotency',
    subFlows: [
      { name: 'Daily Crons', tests: ['CMP-01', 'CMP-02', 'CMP-03', 'CMP-04', 'CMP-05', 'CMP-06'] },
      { name: 'Event-Based Crons', tests: ['CMP-07', 'CMP-08'] },
      { name: 'System & Logging', tests: ['CMP-10', 'CMP-12', 'CMP-13', 'CMP-14'] },
    ],
  },
  {
    name: 'Edge Cases & Security',
    icon: '⚡',
    description: 'Race conditions, boundary cases, injection, authorization',
    subFlows: [
      { name: 'Race Conditions', tests: ['EDG-01', 'EDG-02', 'EDG-04', 'EDG-13', 'PCK-20', 'SES-09'] },
      { name: 'Boundary Conditions', tests: ['EDG-03', 'EDG-05', 'EDG-06', 'EDG-08', 'EDG-10', 'EDG-12', 'EDG-14', 'EDG-15', 'EDG-16', 'PCK-24'] },
      { name: 'Security', tests: ['AX-01', 'AX-02', 'AX-03', 'AX-05', 'AX-06', 'AX-07', 'AX-08', 'STU-02'] },
    ],
  },
];

// ─── Local Storage ─────────────────────────────────────────────────────────────

const LS_KEY = 'qa_session_results';

function loadResults(): Record<string, SessionResult> {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}

function saveResults(r: Record<string, SessionResult>) {
  localStorage.setItem(LS_KEY, JSON.stringify(r));
}

// ─── Batch Run History ────────────────────────────────────────────────────────

interface BatchRunRecord { timestamp: string; passed: number; failed: number; total: number; }
const BATCH_HISTORY_KEY = 'qa_batch_history';

function loadBatchHistory(): BatchRunRecord[] {
  try { return JSON.parse(localStorage.getItem(BATCH_HISTORY_KEY) || '[]'); } catch { return []; }
}

function pushBatchHistory(run: BatchRunRecord) {
  const h = loadBatchHistory();
  h.unshift(run);
  localStorage.setItem(BATCH_HISTORY_KEY, JSON.stringify(h.slice(0, 10)));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SEV_COLOR: Record<string, any> = { P0: 'error', P1: 'warning', P2: 'info', P3: 'default' };
const STATUS_BG: Record<string, string> = {
  PASS: '#e8f5e9', FAIL: '#ffebee', SKIP: '#f5f5f5', null: 'transparent',
};

function StatusDot({ status }: { status: string | null }) {
  if (status === 'PASS') return <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />;
  if (status === 'FAIL') return <Cancel sx={{ fontSize: 14, color: 'error.main' }} />;
  if (status === 'SKIP') return <RemoveCircleOutline sx={{ fontSize: 14, color: 'text.disabled' }} />;
  return <RadioButtonUnchecked sx={{ fontSize: 14, color: 'text.disabled' }} />;
}

function flowStats(flow: Flow, all: QATestCase[], results: Record<string, SessionResult>) {
  const ids = flow.subFlows.flatMap(sf => sf.tests);
  const unique = Array.from(new Set(ids));
  const pass = unique.filter(id => results[id]?.status === 'PASS').length;
  const fail = unique.filter(id => results[id]?.status === 'FAIL').length;
  const skip = unique.filter(id => results[id]?.status === 'SKIP').length;
  const tested = pass + fail + skip;
  return { total: unique.length, pass, fail, skip, tested };
}

// ─── Steps generator — derives steps from test data ────────────────────────

function getSteps(test: QATestCase): string[] {
  // Return meaningful testing steps based on test category/id
  const pre: Record<string, string[]> = {
    'AUTH': ['Open the app or Postman', 'Ensure test phone number is available', test.expectedBehavior],
    'NTF': ['Have 2 test devices (1 SS, 1 PP app)', 'Ensure both FCM tokens are registered', `Trigger the action and verify: ${test.expectedBehavior}`],
    'ISO': ['Set up test user with both SS and PP data', 'Switch between app contexts (x-app-source header)', `Verify isolation: ${test.expectedBehavior}`],
    'BKG': ['Ensure user has an active pack with credits', 'Choose a valid upcoming session', `Complete the booking and verify: ${test.expectedBehavior}`],
    'PCK': ['Set up studio with pack definition', 'Use test user account', `Perform the pack action and verify: ${test.expectedBehavior}`],
    'FRZ': ['Ensure user has an active pack', 'Check studio freeze config (monthly cap / duration mode)', `Apply freeze and verify: ${test.expectedBehavior}`],
    'QR': ['Ensure active booking exists for test user', 'Generate QR code from the app or admin panel', `Scan/submit and verify: ${test.expectedBehavior}`],
    'PAR': ['Set up partner API key', 'Use Postman or /partner-api-tester.html', `Call the endpoint and verify: ${test.expectedBehavior}`],
    'WBH': ['Set up ngrok or use test Razorpay webhook', 'Trigger payment event in Razorpay dashboard', `Verify DB state after webhook: ${test.expectedBehavior}`],
    'CMP': ['Check cron schedule in sessions.service.ts', 'Trigger cron manually or wait for scheduled run', `Verify outcome: ${test.expectedBehavior}`],
    'STU': ['Login as super_admin in admin panel', 'Navigate to Studios section', `Perform the action and verify: ${test.expectedBehavior}`],
    'ADM': ['Login as super_admin in admin panel', `Navigate to the relevant section`, `Verify: ${test.expectedBehavior}`],
    'REV': ['Login as super_admin', 'Navigate to Revenue reporting', `Verify: ${test.expectedBehavior}`],
    'INV': ['Complete a pack purchase', 'Check Invoices section or email', `Verify: ${test.expectedBehavior}`],
    'EML': ['Complete a booking or pack purchase', 'Check email inbox for test user', `Verify: ${test.expectedBehavior}`],
    'EDG': ['Set up the specific edge case conditions', 'Execute concurrent or boundary action', `Verify: ${test.expectedBehavior}`],
    'AX': ['Open API in Postman or browser dev tools', 'Inject malicious payload or unauthorized request', `Verify: ${test.expectedBehavior}`],
    'EVT': ['Navigate to Events section', 'Set up or use existing event', `Perform and verify: ${test.expectedBehavior}`],
    'USR': ['Use test user account', `Perform the action and verify: ${test.expectedBehavior}`],
    'SES': ['Navigate to Sessions section (app or admin)', `Set up required session config`, `Verify: ${test.expectedBehavior}`],
  };
  const prefix = test.id.replace(/[-\d]/g, '').toUpperCase();
  return pre[prefix] || [`Set up: ${test.category}`, `Action: trigger "${test.title}"`, `Verify: ${test.expectedBehavior}`];
}

function getPreConditions(test: QATestCase): string[] {
  const conds: Record<string, string[]> = {
    'NTF': ['User must have FCM token registered for the relevant app', 'Booking must have correct bookingSource set'],
    'ISO': ['Test data exists in BOTH SS and PP contexts', 'Two separate test accounts or app contexts ready'],
    'BKG': ['Active pack with ≥1 credit available', 'Upcoming session with capacity > 0'],
    'FRZ': ['Active pack with credits remaining', 'Studio freeze config verified (admin panel → Studio → Whitelabel)'],
    'QR': ['Valid booking exists (status=booked)', 'QR code generated within 5-minute validity window'],
    'PAR': ['Valid X-Api-Key header configured', 'Partner studio linked to API key'],
    'WBH': ['Razorpay test mode active', 'Webhook endpoint accessible (ngrok for local)'],
    'CMP': ['NODE_ENV != test (crons disabled in test env)', 'DB has records that match cron criteria'],
    'ADM': ['Logged in as super_admin in admin panel'],
    'REV': ['Transactions exist covering the test period'],
  };
  const prefix = test.id.replace(/[-\d]/g, '').toUpperCase();
  return conds[prefix] || [];
}

// ─── Main Component ────────────────────────────────────────────────────────────

const QADashboardPage: React.FC = () => {
  const [tests, setTests] = useState<QATestCase[]>([]);
  const [results, setResults] = useState<Record<string, SessionResult>>(loadResults);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [expandedFlows, setExpandedFlows] = useState<Set<string>>(new Set([FLOW_STRUCTURE[0].name]));
  const [runningId, setRunningId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, passed: 0, failed: 0 });
  const [batchHistory, setBatchHistory] = useState<BatchRunRecord[]>(loadBatchHistory);

  const testMap = useMemo(() => {
    const m: Record<string, QATestCase> = {};
    tests.forEach(t => { m[t.id] = t; });
    return m;
  }, [tests]);

  const runnableTests = useMemo(() => tests.filter(t => t.automatable && !!t.runId), [tests]);

  const selectedTest = selectedId ? testMap[selectedId] : null;
  const selectedResult = selectedId ? results[selectedId] : null;

  // ── Load tests ──────────────────────────────────────────────────────────────

  const loadTests = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res: any = await apiService.get(API_ENDPOINTS.QA.TEST_CASES);
      setTests(res.data ?? res);
    } catch (err: any) {
      setApiError(err?.response?.data?.message || err?.message || 'Backend not reachable — restart backend server');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadTests(); }, [loadTests]);

  // Sync notes from selected result
  useEffect(() => {
    setNotes(results[selectedId ?? '']?.notes ?? '');
  }, [selectedId, results]);

  // ── Result mutation ─────────────────────────────────────────────────────────

  const markResult = (testId: string, status: 'PASS' | 'FAIL' | 'SKIP' | null) => {
    setResults(prev => {
      const next = {
        ...prev,
        [testId]: { ...prev[testId], status, notes: prev[testId]?.notes ?? '', runAt: new Date().toISOString() },
      };
      saveResults(next);
      return next;
    });
  };

  const saveNotes = (testId: string, text: string) => {
    setResults(prev => {
      const next = { ...prev, [testId]: { ...prev[testId], status: prev[testId]?.status ?? null, notes: text } };
      saveResults(next);
      return next;
    });
  };

  const runAutoTest = async (test: QATestCase) => {
    if (!test.runId) return;
    setRunningId(test.id);
    try {
      const res: any = await apiService.post(API_ENDPOINTS.QA.RUN_TEST(test.runId), {});
      const autoResult = res.data ?? res;
      const autoStatus: 'PASS' | 'FAIL' = autoResult.status === 'PASS' ? 'PASS' : 'FAIL';
      setResults(prev => {
        const next: Record<string, SessionResult> = {
          ...prev,
          [test.id]: {
            ...prev[test.id],
            notes: prev[test.id]?.notes ?? '',
            status: autoStatus,
            autoResult,
            runAt: new Date().toISOString(),
          },
        };
        saveResults(next);
        return next;
      });
    } catch (e: any) {
      setResults(prev => {
        const next: Record<string, SessionResult> = {
          ...prev,
          [test.id]: {
            ...prev[test.id],
            notes: prev[test.id]?.notes ?? '',
            status: 'FAIL',
            autoResult: { status: 'ERROR', detail: e.message, durationMs: 0 },
            runAt: new Date().toISOString(),
          },
        };
        saveResults(next);
        return next;
      });
    }
    setRunningId(null);
  };

  // ── Batch runner ────────────────────────────────────────────────────────────

  const runTestRaw = async (test: QATestCase): Promise<'PASS' | 'FAIL'> => {
    try {
      const res: any = await apiService.post(API_ENDPOINTS.QA.RUN_TEST(test.runId!), {});
      const autoResult = res.data ?? res;
      const status: 'PASS' | 'FAIL' = autoResult.status === 'PASS' ? 'PASS' : 'FAIL';
      setResults(prev => {
        const next = { ...prev, [test.id]: { ...prev[test.id], notes: prev[test.id]?.notes ?? '', status, autoResult, runAt: new Date().toISOString() } };
        saveResults(next);
        return next;
      });
      return status;
    } catch (e: any) {
      setResults(prev => {
        const next: Record<string, SessionResult> = { ...prev, [test.id]: { ...prev[test.id], notes: prev[test.id]?.notes ?? '', status: 'FAIL' as const, autoResult: { status: 'ERROR', detail: e.message, durationMs: 0 }, runAt: new Date().toISOString() } };
        saveResults(next);
        return next;
      });
      return 'FAIL';
    }
  };

  const runBatch = async () => {
    if (batchRunning || runnableTests.length === 0) return;
    setBatchRunning(true);
    let passed = 0, failed = 0;
    setBatchProgress({ done: 0, total: runnableTests.length, passed: 0, failed: 0 });
    for (let i = 0; i < runnableTests.length; i++) {
      setRunningId(runnableTests[i].id);
      const status = await runTestRaw(runnableTests[i]);
      if (status === 'PASS') passed++; else failed++;
      setBatchProgress({ done: i + 1, total: runnableTests.length, passed, failed });
    }
    setRunningId(null);
    setBatchRunning(false);
    const record: BatchRunRecord = { timestamp: new Date().toISOString(), passed, failed, total: runnableTests.length };
    pushBatchHistory(record);
    setBatchHistory(loadBatchHistory());
  };

  // ── Global stats ────────────────────────────────────────────────────────────

  const allIds = useMemo(() => tests.map(t => t.id), [tests]);
  const pass = allIds.filter(id => results[id]?.status === 'PASS').length;
  const fail = allIds.filter(id => results[id]?.status === 'FAIL').length;
  const skip = allIds.filter(id => results[id]?.status === 'SKIP').length;
  const tested = pass + fail + skip;
  const total = allIds.length;
  const pct = total > 0 ? Math.round((tested / total) * 100) : 0;

  // ── Filter tree ─────────────────────────────────────────────────────────────

  function isVisible(id: string): boolean {
    const t = testMap[id];
    if (!t) return false;
    if (search && !t.id.toLowerCase().includes(search.toLowerCase()) &&
        !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'untested') return !results[id]?.status;
    if (filter === 'pass') return results[id]?.status === 'PASS';
    if (filter === 'fail') return results[id]?.status === 'FAIL';
    if (filter === 'automated') return !!t.automatable && !!t.runId;
    return true;
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  const exportCSV = () => {
    const rows = tests.map(t => {
      const r = results[t.id];
      return [t.id, t.category, t.severity, `"${t.title}"`, r?.status ?? 'UNTESTED', `"${r?.notes ?? ''}"`, r?.runAt ?? ''].join(',');
    });
    const csv = ['ID,Category,Severity,Title,Status,Notes,TestedAt', ...rows].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `qa_results_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: '#f8f9fa' }}>
      {/* ─ Top bar ─────────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e0e0e0', zIndex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Science color="primary" sx={{ fontSize: 24 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>QA Testing Dashboard</Typography>
              <Typography variant="caption" color="text.secondary">
                {tested}/{total} tested · {pass} pass · {fail} fail · {skip} skip
              </Typography>
            </Box>
            <Box sx={{ width: 120 }}>
              <LinearProgress
                variant="determinate"
                value={pct}
                color={fail > 0 ? 'error' : 'success'}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">{pct}% done</Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            <TextField
              size="small"
              placeholder="Search ID or title..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ fontSize: 16, mr: 0.5, color: 'text.disabled' }} /> }}
              sx={{ width: 200 }}
            />
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="untested">Untested</ToggleButton>
              <ToggleButton value="pass" sx={{ color: 'success.main' }}>Pass</ToggleButton>
              <ToggleButton value="fail" sx={{ color: 'error.main' }}>Fail</ToggleButton>
              <ToggleButton value="automated">🤖 Auto</ToggleButton>
            </ToggleButtonGroup>
            {runnableTests.length > 0 && (
              <Button
                size="small"
                variant="contained"
                color="info"
                startIcon={batchRunning ? <CircularProgress size={13} color="inherit" /> : <PlayArrow />}
                onClick={runBatch}
                disabled={batchRunning}
                sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
              >
                {batchRunning
                  ? `${batchProgress.done}/${batchProgress.total}`
                  : `Run Auto (${runnableTests.length})`}
              </Button>
            )}
            <Tooltip title="Reset all session results">
              <Button
                size="small"
                color="warning"
                onClick={() => {
                  if (window.confirm('Clear all test results for this session?')) {
                    setResults({});
                    localStorage.removeItem(LS_KEY);
                  }
                }}
              >Reset</Button>
            </Tooltip>
            <Tooltip title="Export results as CSV">
              <IconButton size="small" onClick={exportCSV}><FileDownload /></IconButton>
            </Tooltip>
            <IconButton size="small" onClick={loadTests}><Refresh /></IconButton>
          </Stack>
        </Stack>
      </Paper>

      {/* ─ Tab switcher ────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: '1px solid #e0e0e0', bgcolor: 'white', px: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 38 }}>
          <Tab label={`Test Cases (${tests.length})`} sx={{ minHeight: 38, fontSize: 13, py: 0 }} />
          <Tab label="API Health" sx={{ minHeight: 38, fontSize: 13, py: 0 }} />
        </Tabs>
      </Box>

      {apiError && (
        <Alert severity="error" onClose={() => setApiError(null)} sx={{ mx: 2, mt: 1, fontSize: 12 }}>
          {apiError}
        </Alert>
      )}

      {/* ─ Batch run history strip (test cases tab only) ─────────────────── */}
      {activeTab === 0 && batchHistory.length > 0 && (
        <Box sx={{ px: 2, py: 0.75, bgcolor: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography fontSize={11} color="text.secondary" whiteSpace="nowrap">Batch runs:</Typography>
          {batchHistory.slice(0, 6).map((r, i) => (
            <Chip
              key={i}
              size="small"
              label={`${r.passed}/${r.total} ✓  ${new Date(r.timestamp).toLocaleDateString()} ${new Date(r.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
              color={r.failed === 0 ? 'success' : r.passed === 0 ? 'error' : 'warning'}
              variant="outlined"
              sx={{ fontSize: 10, height: 18, '& .MuiChip-label': { px: 0.75 } }}
            />
          ))}
        </Box>
      )}

      {/* ─ Batch progress bar (test cases tab only) ──────────────────────── */}
      {activeTab === 0 && batchRunning && (
        <Box sx={{ px: 2, pt: 0.75, pb: 0.5, bgcolor: '#fff' }}>
          <LinearProgress
            variant="determinate"
            value={batchProgress.total > 0 ? (batchProgress.done / batchProgress.total) * 100 : 0}
            sx={{ height: 4, borderRadius: 2 }}
          />
          <Typography fontSize={11} color="text.secondary" mt={0.25}>
            {batchProgress.done}/{batchProgress.total} · {batchProgress.passed} passed · {batchProgress.failed} failed
          </Typography>
        </Box>
      )}

      {/* ─ API Health tab content ─────────────────────────────────────────── */}
      {activeTab === 1 && (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <ApiHealthTab />
        </Box>
      )}

      {/* ─ Main split panel ────────────────────────────────────────────────── */}
      {activeTab === 0 && <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left: Flow Tree */}
        <Box sx={{ width: 340, flexShrink: 0, borderRight: '1px solid #e0e0e0', overflow: 'auto', bgcolor: '#fff' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
          ) : (
            FLOW_STRUCTURE.map(flow => {
              const stats = flowStats(flow, tests, results);
              const isOpen = expandedFlows.has(flow.name);
              const toggleFlow = () => setExpandedFlows(prev => {
                const next = new Set(prev);
                isOpen ? next.delete(flow.name) : next.add(flow.name);
                return next;
              });

              const flowFail = flow.subFlows.flatMap(sf => sf.tests)
                .filter(id => results[id]?.status === 'FAIL').length;
              const visibleSubFlows = flow.subFlows.map(sf => ({
                ...sf, visibleTests: sf.tests.filter(isVisible),
              })).filter(sf => sf.visibleTests.length > 0);

              if (filter !== 'all' && search !== '' && visibleSubFlows.length === 0) return null;

              return (
                <Box key={flow.name} sx={{ borderBottom: '1px solid #f0f0f0' }}>
                  <Box
                    onClick={toggleFlow}
                    sx={{
                      px: 1.5, py: 1, cursor: 'pointer', display: 'flex',
                      alignItems: 'center', gap: 1,
                      bgcolor: isOpen ? '#f0f4ff' : 'transparent',
                      '&:hover': { bgcolor: '#f5f5f5' },
                    }}
                  >
                    <Typography fontSize={16}>{flow.icon}</Typography>
                    <Box flex={1} minWidth={0}>
                      <Typography fontSize={12} fontWeight={700} noWrap>{flow.name}</Typography>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <LinearProgress
                          variant="determinate"
                          value={stats.total > 0 ? (stats.tested / stats.total) * 100 : 0}
                          color={flowFail > 0 ? 'error' : 'success'}
                          sx={{ flex: 1, height: 4, borderRadius: 2 }}
                        />
                        <Typography fontSize={10} color="text.secondary" whiteSpace="nowrap">
                          {stats.tested}/{stats.total}
                        </Typography>
                        {flowFail > 0 && <Chip label={`${flowFail}✗`} size="small" color="error" sx={{ height: 14, fontSize: 9 }} />}
                      </Stack>
                    </Box>
                    <ExpandMore sx={{ fontSize: 16, transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                  </Box>

                  {isOpen && (
                    <Box>
                      {flow.subFlows.map(sf => {
                        const visible = sf.tests.filter(isVisible);
                        if (visible.length === 0) return null;
                        const sfPass = sf.tests.filter(id => results[id]?.status === 'PASS').length;
                        const sfFail = sf.tests.filter(id => results[id]?.status === 'FAIL').length;
                        return (
                          <Box key={sf.name}>
                            <Box sx={{ px: 2, py: 0.5, bgcolor: '#fafafa', borderTop: '1px solid #f5f5f5' }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography fontSize={11} color="text.secondary" fontWeight={600}>{sf.name}</Typography>
                                <Typography fontSize={10} color="text.disabled">
                                  {sfPass > 0 && <span style={{ color: '#2e7d32' }}>✓{sfPass} </span>}
                                  {sfFail > 0 && <span style={{ color: '#c62828' }}>✗{sfFail} </span>}
                                  {sf.tests.length - sfPass - sfFail} left
                                </Typography>
                              </Stack>
                            </Box>
                            {visible.map(id => {
                              const t = testMap[id];
                              if (!t) return null;
                              const r = results[id];
                              const isSelected = selectedId === id;
                              return (
                                <Box
                                  key={id}
                                  onClick={() => { setSelectedId(id); setNotes(r?.notes ?? ''); }}
                                  sx={{
                                    px: 2, py: 0.6, cursor: 'pointer', display: 'flex',
                                    alignItems: 'flex-start', gap: 0.8,
                                    bgcolor: isSelected ? '#e8f0fe' : STATUS_BG[r?.status ?? 'null'],
                                    borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                                    '&:hover': { bgcolor: isSelected ? '#e8f0fe' : '#f5f5f5' },
                                  }}
                                >
                                  <StatusDot status={r?.status ?? null} />
                                  <Box flex={1} minWidth={0}>
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                      <Typography fontSize={10} fontFamily="monospace" color="text.secondary">{id}</Typography>
                                      <Chip
                                        label={t.severity}
                                        size="small"
                                        color={SEV_COLOR[t.severity]}
                                        sx={{ height: 12, fontSize: 9, '& .MuiChip-label': { px: 0.5 } }}
                                      />
                                      {t.automatable && <Typography fontSize={9} color="primary.main">🤖</Typography>}
                                      {t.bugRef && !t.fixedInVersion && <Typography fontSize={9} color="error.main">🐛</Typography>}
                                      {t.bugRef && t.fixedInVersion && <Typography fontSize={9} color="success.main">✅</Typography>}
                                    </Stack>
                                    <Typography fontSize={11} lineHeight={1.3} color={isSelected ? 'primary.main' : 'text.primary'}>
                                      {t.title}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Right: Test Detail */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5, bgcolor: '#f8f9fa' }} hidden={activeTab !== 0}>
          {!selectedTest ? (
            <Box
              display="flex" flexDirection="column" alignItems="center"
              justifyContent="center" height="100%" color="text.disabled" gap={1}
            >
              <Science sx={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="h6" color="text.disabled">Select a test from the left panel</Typography>
              <Typography variant="body2" color="text.disabled">Click any test case to see full details and mark results</Typography>
            </Box>
          ) : (
            <Box maxWidth={760}>
              {/* Header */}
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                <Box>
                  <Stack direction="row" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                    <Typography fontFamily="monospace" fontWeight={700} color="primary">{selectedTest.id}</Typography>
                    <Chip label={selectedTest.severity} size="small" color={SEV_COLOR[selectedTest.severity]} />
                    <Chip label={selectedTest.category} size="small" variant="outlined" />
                    {selectedTest.automatable && <Chip label="🤖 Automatable" size="small" color="info" />}
                    {selectedTest.bugRef && (
                      <Chip
                        icon={<BugReport sx={{ fontSize: 12 }} />}
                        label={`${selectedTest.bugRef}${selectedTest.fixedInVersion ? ' ✓ Fixed' : ' Open'}`}
                        size="small"
                        color={selectedTest.fixedInVersion ? 'success' : 'error'}
                      />
                    )}
                  </Stack>
                  <Typography variant="h6" fontWeight={600} lineHeight={1.3}>{selectedTest.title}</Typography>
                </Box>
                <Stack direction="row" gap={0.5}>
                  <Tooltip title="Previous test"><span /></Tooltip>
                </Stack>
              </Stack>

              {/* Pre-conditions */}
              {getPreConditions(selectedTest).length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff8e1', borderColor: '#ffe082' }}>
                  <Typography fontSize={12} fontWeight={700} color="warning.dark" mb={0.5}>⚠ Pre-conditions</Typography>
                  {getPreConditions(selectedTest).map((c, i) => (
                    <Typography key={i} fontSize={12} color="warning.dark">• {c}</Typography>
                  ))}
                </Paper>
              )}

              {/* Steps */}
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff' }}>
                <Typography fontSize={12} fontWeight={700} mb={1}>Steps to Test</Typography>
                {getSteps(selectedTest).map((step, i) => (
                  <Stack key={i} direction="row" gap={1} mb={0.5} alignItems="flex-start">
                    <Box sx={{
                      width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, flexShrink: 0, mt: 0.1,
                    }}>{i + 1}</Box>
                    <Typography fontSize={13}>{step}</Typography>
                  </Stack>
                ))}
              </Paper>

              {/* Expected behavior */}
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#e8f5e9', borderColor: '#a5d6a7' }}>
                <Typography fontSize={12} fontWeight={700} color="success.dark" mb={0.3}>✓ Expected Result</Typography>
                <Typography fontSize={13} color="success.dark">{selectedTest.expectedBehavior}</Typography>
              </Paper>

              {/* Source */}
              {selectedTest.source && (
                <Paper variant="outlined" sx={{ p: 1, mb: 1.5, bgcolor: '#f5f5f5' }}>
                  <Typography fontSize={11} color="text.secondary">
                    📄 <code>{selectedTest.source}</code>
                  </Typography>
                </Paper>
              )}

              {/* Bug reference */}
              {selectedTest.bugRef && (
                <Alert
                  severity={selectedTest.fixedInVersion ? 'success' : 'warning'}
                  sx={{ mb: 1.5, fontSize: 12 }}
                >
                  <strong>{selectedTest.bugRef}</strong>
                  {selectedTest.fixedInVersion
                    ? ` — Fixed ${selectedTest.fixedInVersion}. This test verifies the fix is in place.`
                    : ' — Known open bug. This test is expected to FAIL until fixed.'}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Pass / Fail / Skip */}
              <Typography fontSize={13} fontWeight={700} mb={1}>Mark Result</Typography>
              <Stack direction="row" gap={1.5} mb={2} alignItems="center">
                <Button
                  variant={selectedResult?.status === 'PASS' ? 'contained' : 'outlined'}
                  color="success"
                  size="large"
                  startIcon={<CheckCircle />}
                  onClick={() => markResult(selectedTest.id, 'PASS')}
                  sx={{ minWidth: 100 }}
                >Pass</Button>
                <Button
                  variant={selectedResult?.status === 'FAIL' ? 'contained' : 'outlined'}
                  color="error"
                  size="large"
                  startIcon={<Cancel />}
                  onClick={() => markResult(selectedTest.id, 'FAIL')}
                  sx={{ minWidth: 100 }}
                >Fail</Button>
                <Button
                  variant={selectedResult?.status === 'SKIP' ? 'contained' : 'outlined'}
                  color="inherit"
                  size="large"
                  startIcon={<RemoveCircleOutline />}
                  onClick={() => markResult(selectedTest.id, 'SKIP')}
                >Skip</Button>
                {selectedResult?.status && (
                  <Button size="small" onClick={() => markResult(selectedTest.id, null)} color="inherit">
                    Clear
                  </Button>
                )}
                {selectedResult?.runAt && (
                  <Typography fontSize={11} color="text.disabled" ml="auto">
                    Marked {new Date(selectedResult.runAt).toLocaleTimeString()}
                  </Typography>
                )}
              </Stack>

              {/* Notes */}
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                label="Notes / Actual result / Bug details"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                onBlur={() => saveNotes(selectedTest.id, notes)}
                placeholder="Describe what actually happened, or paste error message..."
                sx={{ mb: 2 }}
              />

              {/* Automated test runner */}
              {selectedTest.automatable && selectedTest.runId && (
                <Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" alignItems="center" gap={1.5} mb={1}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={runningId === selectedTest.id ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                      onClick={() => runAutoTest(selectedTest)}
                      disabled={runningId === selectedTest.id}
                    >
                      {runningId === selectedTest.id ? 'Running...' : 'Run Automated Check'}
                    </Button>
                    <Typography fontSize={12} color="text.secondary">
                      Backend verifies this automatically
                    </Typography>
                  </Stack>

                  {selectedResult?.autoResult && (
                    <Alert
                      severity={selectedResult.autoResult.status === 'PASS' ? 'success' : 'error'}
                      sx={{ fontSize: 12 }}
                    >
                      <strong>{selectedResult.autoResult.status}</strong>: {selectedResult.autoResult.detail}
                      {selectedResult.autoResult.durationMs > 0 && (
                        <span style={{ opacity: 0.7 }}> ({selectedResult.autoResult.durationMs}ms)</span>
                      )}
                    </Alert>
                  )}
                </Box>
              )}

              {/* Navigate to next untested in same flow */}
              {(() => {
                const currentFlow = FLOW_STRUCTURE.find(f =>
                  f.subFlows.some(sf => sf.tests.includes(selectedTest.id))
                );
                if (!currentFlow) return null;
                const allFlowTests = currentFlow.subFlows.flatMap(sf => sf.tests);
                const nextUntested = allFlowTests.find(id => id !== selectedTest.id && !results[id]?.status && testMap[id]);
                if (!nextUntested) return null;
                return (
                  <Box mt={2}>
                    <Divider sx={{ mb: 1 }} />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => { setSelectedId(nextUntested); setNotes(results[nextUntested]?.notes ?? ''); }}
                    >
                      Next untested in {currentFlow.name} → {nextUntested}
                    </Button>
                  </Box>
                );
              })()}
            </Box>
          )}
        </Box>
      </Box>}
    </Box>
  );
};

export default QADashboardPage;
